/**
 * Ensures that a set of always-present columns exist in a (possibly cached)
 * view's column list. Cached views persisted before a new immutable column was
 * introduced would otherwise omit it entirely, since the ViewTable engine only
 * renders columns listed in `view.columns`. Any missing required column is
 * inserted at the position it holds in `defaultColumns`, so a reserved
 * pinned-left column lands back at the start rather than at the end.
 */
export const ensureRequiredColumns = <TColumn extends { id: string }>(
    columns: TColumn[],
    defaultColumns: TColumn[],
    requiredColumnIds: readonly string[],
): TColumn[] => {
    const presentIds = new Set(columns.map((column) => column.id));
    const missingRequiredIds = requiredColumnIds.filter(
        (id) => !presentIds.has(id),
    );
    if (missingRequiredIds.length === 0) {
        return columns;
    }

    const result = [...columns];
    for (const requiredId of missingRequiredIds) {
        const defaultIndex = defaultColumns.findIndex(
            (column) => column.id === requiredId,
        );
        const defaultColumn = defaultColumns[defaultIndex];
        if (!defaultColumn) {
            continue;
        }
        const insertAt = Math.min(defaultIndex, result.length);
        result.splice(insertAt, 0, defaultColumn);
    }
    return result;
};
